import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    DialogCloseIconColorConstantType,
    DialogMaxWidthConstantType,
    DialogDynamicStyleType,
    DialogTypeRoles,
    DialogSize,
    DialogType
} from "./type";
import type {
    INCoreUIKitIconProps,
    Mutable
} from "../../types";
import {
    getModalSurfaceStyle,
    getModalGutterStyle
} from "../modal/stylesheet";
import {
    getFooterBandStyle
} from "../../helpers/theme/footerBand";
import {
    getStateColorKey
} from "../../helpers/theme/state";

export const DIALOG_TYPE_ROLES: Record<DialogType, DialogTypeRoles> = {
    info: {
        primaryButtonType: "primary",
        role: "dialog"
    },
    neutral: {
        primaryButtonType: "primary",
        role: "dialog"
    },
    primary: {
        primaryButtonType: "primary",
        role: "dialog"
    },
    success: {
        primaryButtonType: "primary",
        role: "dialog"
    },
    warning: {
        primaryButtonType: "primary",
        role: "dialog"
    },
    danger: {
        primaryButtonType: "danger",
        role: "alertdialog"
    }
};

export const DIALOG_SIZE_RATIOS: Record<DialogSize, number> = {
    xSmall: 2 / 3,
    large: 4 / 3,
    small: 5 / 6,
    medium: 1
};

export const getDialogMaxWidth = ({
    shapeConfigs,
    size
}: DialogMaxWidthConstantType): number => {
    return Math.round(shapeConfigs.dialogMaxWidth * DIALOG_SIZE_RATIOS[size]);
};

export const getDialogCloseIconColor = ({
    closeIconProps,
    colors
}: DialogCloseIconColorConstantType): INCoreUIKitIconProps["customColor"] => {
    if(closeIconProps?.customColor) {
        const customColor = closeIconProps.customColor as keyof NCoreUIKit.ProjectColorPalette;

        return colors.project[customColor] ? colors.project[customColor] : closeIconProps.customColor;
    }

    if(closeIconProps?.color) {
        return colors.content.icon[closeIconProps.color];
    }

    return undefined;
};

const stylesheet = StyleSheet.create({
    bottomContentContainer: {
        justifyContent: "flex-end",
        flexDirection: "row"
    },
    gutter: {
        pointerEvents: "box-none",
        alignItems: "center",
        alignSelf: "stretch",
        flexShrink: 1
    },
    iconTile: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    languageHeader: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    languageHeaderTexts: {
        flexDirection: "column",
        flex: 1,
        flexShrink: 1
    },
    container: {
        flexDirection: "column",
        borderStyle: "solid",
        flexShrink: 1,
        width: "100%"
    },
    headerContainer: {
        flexDirection: "column"
    },
    scrollContent: {
        flexDirection: "column"
    },
    content: {
        flexDirection: "column"
    },
    centeredHeader: {
        alignSelf: "stretch",
        alignItems: "center"
    },
    centeredContent: {
        alignItems: "center"
    },
    closeIcon: {
        position: "absolute"
    },
    centeredText: {
        textAlign: "center"
    },
    bottomContainer: {
        flexShrink: 0
    },
    scroll: {
        flexShrink: 1,
        flexGrow: 0
    },
    headerTitle: {
        flexShrink: 1
    }
});

export const useStyles = ({
    contentJustify = "centered",
    controlHeights,
    alignContent,
    shapeConfigs,
    isShowClose,
    shadowAlpha,
    closeStyle,
    elevations,
    radiuses,
    borders,
    colors,
    spaces,
    size,
    type
}: DialogDynamicStyleType) => {
    const stateKey = getStateColorKey({
        type
    });

    const styles = {
        iconTile: {
            backgroundColor: colors.system.container[stateKey],
            borderRadius: radiuses.actions,
            height: controlHeights.medium,
            width: controlHeights.medium
        } as Mutable<ViewStyle>,
        bottomContainer: {
            ...getFooterBandStyle({
                surfaceBorderWidth: borders.line,
                outerRadius: radiuses.surface,
                inset: spaces.spacingLg,
                shapeConfigs,
                borders,
                colors,
                spaces
            }),
            marginTop: spaces.spacingLg - spaces.spacingMd,
            marginHorizontal: -spaces.spacingLg,
            marginBottom: -spaces.spacingLg
        } as Mutable<ViewStyle>,
        container: {
            ...getModalSurfaceStyle({
                shapeConfigs,
                shadowAlpha,
                elevations,
                radiuses,
                borders,
                colors,
                spaces
            }),
            maxWidth: getDialogMaxWidth({
                shapeConfigs,
                size
            })
        } as Mutable<ViewStyle>,
        gutter: getModalGutterStyle({
            alignContent,
            spaces
        }) as Mutable<ViewStyle>,
        languageHeader: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        languageHeaderTexts: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        centeredHeader: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        bottomContentContainer: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        headerContainer: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        scrollContent: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        closeIcon: {
            top: closeStyle.top,
            end: closeStyle.end
        } as Mutable<ViewStyle>
    };

    if(!isShowClose) {
        return styles;
    }

    styles.headerContainer.paddingEnd = spaces.spacingXl;

    if(contentJustify === "centered") {
        styles.headerContainer.paddingStart = spaces.spacingXl;
    }

    return styles;
};
export default stylesheet;
